import { CheckOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Modal, Select, Table, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import { getHealthMetricDescription } from '../../utils/utilFunctions';

const { Text } = Typography;
const { Option } = Select;

export interface ParsedBloodworkResult {
  extractedName: string;
  value: number;
  unit: string;
  originalValue?: string;
  matchedHealthMetricType?: HealthMetricType;
  confidence: number;
}

interface BloodworkImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onImport: (selectedResults: ParsedBloodworkResult[]) => void;
  results: ParsedBloodworkResult[];
  healthmetrictypes: HealthMetricType[];
}

export const BloodworkImportModal = ({
  visible,
  onCancel,
  onImport,
  results,
  healthmetrictypes,
}: BloodworkImportModalProps) => {
  const [excludedResults, setExcludedResults] = useState<ParsedBloodworkResult[]>([]);
  const [editableValues, setEditableValues] = useState<{ [key: string]: number }>({});
  const [overriddenTypes, setOverriddenTypes] = useState<{ [key: string]: HealthMetricType | null }>({});
  const { t, i18n } = useTranslation();

  const handleCheckboxChange = (result: ParsedBloodworkResult, checked: boolean) => {
    if (checked) {
      // If checked, add to excluded (don't keep)
      setExcludedResults([...excludedResults, result]);
    } else {
      // If unchecked, remove from excluded (keep)
      setExcludedResults(excludedResults.filter((r) => r !== result));
    }
  };

  const handleValueChange = (record: ParsedBloodworkResult, value: number) => {
    const key = `${record.extractedName}-${record.value}-${record.unit}`;
    setEditableValues({ ...editableValues, [key]: value });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'green';
    if (confidence >= 50) return 'orange';
    return 'red';
  };

  const getConfidenceText = (confidence: number) => {
    if (confidence >= 80) return 'High';
    if (confidence >= 50) return 'Medium';
    return 'Low';
  };

  const handleTypeOverride = (record: ParsedBloodworkResult, healthMetricTypeId: number | null) => {
    const key = `${record.extractedName}-${record.value}-${record.unit}`;
    const selectedType = healthMetricTypeId
      ? healthmetrictypes.find((type) => type.id === healthMetricTypeId) || null
      : null;
    setOverriddenTypes({ ...overriddenTypes, [key]: selectedType });
  };

  const getEffectiveHealthMetricType = (record: ParsedBloodworkResult): HealthMetricType | undefined => {
    const key = `${record.extractedName}-${record.value}-${record.unit}`;
    const overridden = overriddenTypes[key];
    if (overridden !== undefined) {
      return overridden || undefined;
    }
    return record.matchedHealthMetricType;
  };

  const handleImport = () => {
    // Get all results that are NOT excluded (i.e., the ones to keep)
    const resultsToImport = results
      .filter((result) => !excludedResults.includes(result))
      .map((result) => {
        const key = `${result.extractedName}-${result.value}-${result.unit}`;
        const effectiveType = getEffectiveHealthMetricType(result);
        return {
          ...result,
          value: editableValues[key] ?? result.value,
          matchedHealthMetricType: effectiveType,
        };
      });
    onImport(resultsToImport);
    setExcludedResults([]);
    setEditableValues({});
    setOverriddenTypes({});
  };

  const copyUnmatchedMeasurements = () => {
    const unmatchedMeasurements = results
      .filter((result) => !result.matchedHealthMetricType)
      .map((result) => result.extractedName);

    if (unmatchedMeasurements.length === 0) {
      message.info(t('healthMetric.noUnmatchedMeasurementsFound'));
      return;
    }

    const textToCopy = unmatchedMeasurements.join('\n');
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success(t('healthMetric.copiedUnmatchedMeasurementNames', { count: unmatchedMeasurements.length }));
      })
      .catch(() => {
        message.error(t('healthMetric.failedToCopyToClipboard'));
      });
  };

  const columns = [
    {
      title: t('healthMetric.exclude'),
      dataIndex: 'exclude',
      key: 'exclude',
      width: 80,
      render: (_: any, record: ParsedBloodworkResult) => (
        <Checkbox
          checked={excludedResults.includes(record)}
          onChange={(e) => handleCheckboxChange(record, e.target.checked)}
        />
      ),
    },
    {
      title: t('healthMetric.detectedName'),
      dataIndex: 'extractedName',
      key: 'extractedName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: t('healthMetric.matchedType'),
      dataIndex: 'matchedHealthMetricType',
      key: 'matchedType',
      width: 300,
      render: (_: any, record: ParsedBloodworkResult) => {
        const key = `${record.extractedName}-${record.value}-${record.unit}`;
        const effectiveType = getEffectiveHealthMetricType(record);
        const overridden = overriddenTypes[key] !== undefined;
        const isOverridden = overridden && overriddenTypes[key]?.id !== record.matchedHealthMetricType?.id;

        const customFilterOption = (inputValue: string, option: any) => {
          const searchText = inputValue.toLowerCase();
          // Check multiple possible locations for the text
          const childrenText = option?.children?.props?.children?.toLowerCase?.() || '';
          const titleText = option?.title?.toLowerCase?.() || '';
          const keyText = option?.key?.toString().toLowerCase() || '';
          return (
            childrenText.indexOf(searchText) >= 0 ||
            titleText.indexOf(searchText) >= 0 ||
            keyText.indexOf(searchText) >= 0
          );
        };

        return (
          <Select
            style={{ width: '100%' }}
            showSearch
            allowClear
            placeholder={t('healthMetric.selectHealthMetric')}
            value={effectiveType?.id}
            onChange={(value) => handleTypeOverride(record, value)}
            filterOption={customFilterOption}
            status={!effectiveType ? 'warning' : isOverridden ? 'warning' : undefined}
          >
            {healthmetrictypes.map((item) => {
              const description = getHealthMetricDescription(item, i18n.language);
              return (
                <Option key={item.id} value={item.id} title={description}>
                  <span>{description}</span>
                </Option>
              );
            })}
          </Select>
        );
      },
    },
    {
      title: t('healthMetric.value'),
      dataIndex: 'value',
      key: 'value',
      render: (value: number, record: ParsedBloodworkResult) => {
        const key = `${record.extractedName}-${record.value}-${record.unit}`;
        const displayValue = editableValues[key] ?? value;
        const hasOriginalValue = record.originalValue && record.originalValue !== value.toString();

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              type="number"
              value={displayValue}
              onChange={(e) => handleValueChange(record, parseFloat(e.target.value) || 0)}
              suffix={record.unit}
              style={{ width: 150 }}
            />
            {hasOriginalValue && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                (Original: {record.originalValue})
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: t('healthMetric.confidence'),
      dataIndex: 'confidence',
      key: 'confidence',
      render: (confidence: number) => (
        <Tag color={getConfidenceColor(confidence)}>
          {getConfidenceText(confidence)} ({confidence}%)
        </Tag>
      ),
    },
  ];

  return (
    <Modal
      title={t('healthMetric.importBloodworkMeasurements')}
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel} icon={<CloseOutlined />}>
          {t('healthMetric.cancel')}
        </Button>,
        <Button
          key="import"
          type="primary"
          onClick={handleImport}
          disabled={excludedResults.length === results.length}
          icon={<CheckOutlined />}
        >
          {t('healthMetric.import')} ({results.length - excludedResults.length} {t('healthMetric.selected')})
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text>{t('healthMetric.reviewDetectedMeasurements')}</Text>
      </div>

      <Table
        dataSource={results}
        columns={columns}
        rowKey={(record) => `${record.extractedName}-${record.value}-${record.unit}`}
        pagination={false}
        size="small"
        scroll={{ y: 400 }}
      />

      {results.filter((r) => !r.matchedHealthMetricType).length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            backgroundColor: '#fff7e6',
            borderRadius: 6,
            border: '1px solid #ffd591',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text type="warning" strong>
              ⚠️ {results.filter((r) => !r.matchedHealthMetricType).length}{' '}
              {t('healthMetric.unmatchedMeasurementsFound')}
            </Text>
            <Button size="small" icon={<CopyOutlined />} onClick={copyUnmatchedMeasurements} type="dashed">
              {t('healthMetric.copyNames')}
            </Button>
          </div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {t('healthMetric.theseMeasurementsCouldNotBeMatched')}
          </Text>
          <div
            style={{
              backgroundColor: '#fafafa',
              padding: 8,
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: '12px',
              maxHeight: '120px',
              overflowY: 'auto',
            }}
          >
            {results
              .filter((r) => !r.matchedHealthMetricType)
              .map((result, index) => (
                <div key={index} style={{ marginBottom: 2 }}>
                  • {result.extractedName} ({result.value} {result.unit})
                </div>
              ))}
          </div>
        </div>
      )}
    </Modal>
  );
};
